/**
 * TRITAL Care® — Capability Statement Verification
 *
 * Landing page that recipients reach by scanning the QR code on a printed
 * capability statement (or clicking the verify URL). Decodes the fragment
 * payload, displays the timestamped snapshot + integrity hash, and shows
 * a read-only preview of the document exactly as issued.
 */

import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import {
  ShieldCheck, ShieldAlert, ShieldX, Clock, Fingerprint, ArrowRight,
  Download, Calendar, ExternalLink,
} from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import CapabilityDocument from "./CapabilityDocument";
import { decodeCapability } from "../../utils/capabilityLink";

const REASON_COPY = {
  missing: { Icon: ShieldAlert, color: "amber", title: "No verification payload found", body: "This page expects a verification token in the URL. If you reached it from a printed capability statement, please scan the QR code again or re-enter the complete link." },
  malformed: { Icon: ShieldX, color: "red", title: "Malformed verification link", body: "The token in the URL could not be decoded. The link may have been truncated in an email client. Ask the sender to share the full link again." },
  corrupted: { Icon: ShieldX, color: "red", title: "Integrity check failed", body: "The token's checksum does not match its contents — the link may have been edited or truncated. For security, we will not display the snapshot. Request a fresh link from procurement@tritalcare.com." },
  version: { Icon: ShieldAlert, color: "amber", title: "Unsupported document version", body: "This snapshot was generated by an older version of the capability builder. Please ask the sender to regenerate and resend." },
  expired: { Icon: ShieldAlert, color: "amber", title: "Snapshot expired", body: "Capability-statement verification links are valid for 90 days. Please request an updated copy from procurement@tritalcare.com." },
};

export default function VerifyCapability() {
  const location = useLocation();
  const result = useMemo(() => decodeCapability(location.hash), [location.hash]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verify-capability-page">
      <PageMeta
        title="Verify Capability Statement | TRITAL Care®"
        description="Verify a TRITAL Care® capability statement. Open the QR-linked, time-stamped snapshot showing the configuration, certifications, and integrity hash as issued."
        canonical="https://www.tritalcare.com/verify"
        noIndex
      />
      <PublicNav />

      <section className="relative overflow-hidden bg-trital-mesh border-b border-slate-200">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
            <ShieldCheck className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
            <span className="overline text-trital-navy">Capability Statement · Verification</span>
          </div>
          <h1 className="mt-4 font-display font-black text-4xl lg:text-[52px] leading-[1.04] tracking-tight text-slate-900 text-balance">
            {result.ok ? (
              <>Snapshot <span className="text-emerald-700">verified.</span></>
            ) : (
              <>Verification <span className="text-trital-red">unavailable.</span></>
            )}
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
            {result.ok
              ? "This page was generated by scanning the QR code on a TRITAL Care capability statement (or opening the shareable link). The snapshot below is an exact, integrity-verified copy of what the sender issued."
              : "We could not verify the snapshot in this URL. Please review the detail below."}
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        {result.ok ? <VerifiedCard result={result} /> : <ErrorCard result={result} />}
      </section>

      <PublicFooter />

      {/* Print styles — verify snapshot saves as a clean PDF too */}
      <style>{`
        @media print {
          @page { size: Letter; margin: 0; }
          html, body, #root, .App { background: #fff !important; }
          [data-testid="verify-capability-page"] > div:not([data-testid="verify-doc-wrap"]),
          [data-testid="verify-capability-page"] section:not(:has([data-testid="verify-doc-wrap"])),
          [data-testid="verify-ok"] > div:not([data-testid="verify-doc-wrap"]),
          [data-testid="verify-actions"],
          nav, footer { display: none !important; }
          [data-testid="verify-doc-wrap"] { padding: 0 !important; overflow: visible !important; }
          .cap-doc { box-shadow: none !important; border: 0 !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function VerifiedCard({ result }) {
  const { payload, meta } = result;
  const issued = new Date(meta.issuedAt);
  const expires = new Date(meta.expiresAt);
  const selfUrl = typeof window !== "undefined" ? window.location.href : "";

  const [qrDataUrl, setQrDataUrl] = useState(null);
  useEffect(() => {
    if (!selfUrl) return;
    let cancelled = false;
    QRCode.toDataURL(selfUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 320,
      color: { dark: "#0A1F3F", light: "#FFFFFF" },
    })
      .then((u) => { if (!cancelled) setQrDataUrl(u); })
      .catch(() => { if (!cancelled) setQrDataUrl(null); });
    return () => { cancelled = true; };
  }, [selfUrl]);

  return (
    <div className="space-y-8" data-testid="verify-ok">
      {/* Metadata strip */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 lg:p-6 grid md:grid-cols-4 gap-4"
      >
        <StripItem Icon={ShieldCheck} label="Integrity" value="Verified" valueClass="text-emerald-700" testId="verify-integrity" />
        <StripItem Icon={Calendar} label="Issued" value={issued.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })} testId="verify-issued" />
        <StripItem Icon={Clock} label="Valid until" value={expires.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })} testId="verify-expires" />
        <StripItem Icon={Fingerprint} label="Integrity hash" value={meta.integrity} valueClass="font-mono" testId="verify-hash" />
      </motion.div>

      {/* The document itself */}
      <div className="overflow-x-auto pb-2" data-testid="verify-doc-wrap">
        <CapabilityDocument
          recipient={payload.recipient || {}}
          vehicles={payload.vehicles || []}
          naics={payload.naics || []}
          note={payload.note || ""}
          generatedAt={meta.issuedAt}
          integrity={meta.integrity}
          qrDataUrl={qrDataUrl}
          verifyUrl={selfUrl}
        />
      </div>

      {/* Actions */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6 flex flex-wrap items-center gap-3" data-testid="verify-actions">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep"
          data-testid="verify-print"
        >
          <Download className="w-4 h-4" strokeWidth={1.8} /> Save snapshot as PDF
        </button>
        <Link
          to={`/capability-statement${typeof window !== "undefined" ? window.location.hash : ""}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-trital-navy text-[13px] font-semibold hover:bg-slate-50"
          data-testid="verify-open-builder"
        >
          <ExternalLink className="w-4 h-4" strokeWidth={1.8} /> Open in builder
        </Link>
        <Link
          to="/diversity-and-inclusion"
          className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-semibold text-trital-navy/80 hover:text-trital-navy"
          data-testid="verify-goto-diversity"
        >
          About our diversity certifications <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
        </Link>
      </div>
    </div>
  );
}

function ErrorCard({ result }) {
  const copy = REASON_COPY[result.reason] || REASON_COPY.malformed;
  const { Icon } = copy;
  const palettes = {
    amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-700" },
    red:   { bg: "bg-red-50",   border: "border-red-200",   icon: "text-red-700" },
  };
  const pal = palettes[copy.color] || palettes.red;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border ${pal.border} ${pal.bg} p-6 lg:p-8`}
      data-testid={`verify-error-${result.reason}`}
    >
      <div className={`w-12 h-12 rounded-xl bg-white border ${pal.border} ${pal.icon} flex items-center justify-center`}>
        <Icon className="w-6 h-6" strokeWidth={1.8} />
      </div>
      <div className="mt-4 font-display font-black text-slate-900 text-[22px] leading-tight">{copy.title}</div>
      <p className="mt-2 text-slate-700 leading-relaxed max-w-2xl">{copy.body}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link to="/capability-statement" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="verify-error-new">
          Build a new capability statement <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
        </Link>
        <Link to="/diversity-and-inclusion" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-trital-navy text-[13px] font-semibold hover:bg-white" data-testid="verify-error-back">
          Back to Diversity &amp; Inclusion
        </Link>
      </div>
    </motion.div>
  );
}

function StripItem({ Icon, label, value, valueClass = "", testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700/80">
        <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {label}
      </div>
      <div className={`mt-1 font-bold text-slate-900 text-[14px] break-words ${valueClass}`}>{value}</div>
    </div>
  );
}
